import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Send } from "lucide-react";
import { hasPerm, maskPhone } from "@/components/admin/_shared";
import { toast } from "sonner";

export default function InboxTab({ user }) {
  const canPII = hasPerm(user, "orders.view_pii");
  const [threads, setThreads] = useState([]);
  const [activePhone, setActivePhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/admin/inbox/threads");
      setThreads(data.threads || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load inbox");
    } finally {
      setLoadingThreads(false);
    }
  };

  const loadMessages = async (phone) => {
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/admin/inbox/threads/${encodeURIComponent(phone)}`);
      setMessages(data.messages || []);
      await api.patch(`/admin/inbox/threads/${encodeURIComponent(phone)}/read`);
      // Optimistically clear the unread badge in the side list
      setThreads((ts) => ts.map((t) => (t.phone === phone ? { ...t, unread: 0 } : t)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (activePhone) loadMessages(activePhone);
  }, [activePhone]);

  // Poll every 25s so new messages show up without a manual refresh
  useEffect(() => {
    const t = setInterval(() => {
      loadThreads();
      if (activePhone) loadMessages(activePhone);
    }, 25000);
    return () => clearInterval(t);
  }, [activePhone]);

  const sendReply = async (e) => {
    e?.preventDefault();
    if (!reply.trim() || !activePhone) return;
    setSending(true);
    try {
      await api.post(`/admin/inbox/threads/${encodeURIComponent(activePhone)}/reply`, { body: reply.trim() });
      setReply("");
      await loadMessages(activePhone);
      await loadThreads();
      toast.success("Reply sent");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send. Has the 24h window expired? Use a template.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[500px]" data-testid="inbox-tab">
      {/* Thread list */}
      <div className="bg-white border border-[#E5E5E5] overflow-hidden flex flex-col">
        <div className="p-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ WhatsApp</div>
            <div className="font-display font-black text-lg">Inbox</div>
          </div>
          <button onClick={loadThreads} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#525252] hover:text-[#0A0A0A]" data-testid="inbox-refresh">
            Refresh
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingThreads && <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#059669]" /></div>}
          {!loadingThreads && threads.length === 0 && (
            <div className="p-6 text-center text-xs text-[#525252]" data-testid="inbox-empty">
              No conversations yet. Once your WhatsApp webhook is connected, incoming messages will appear here.
            </div>
          )}
          {threads.map((t) => {
            const isActive = t.phone === activePhone;
            const name = t.matched?.name || t.contact_name || (canPII ? t.phone : maskPhone(t.phone));
            return (
              <button
                key={t.phone}
                onClick={() => setActivePhone(t.phone)}
                data-testid={`inbox-thread-${t.phone}`}
                className={`w-full text-left p-3 border-b border-[#E5E5E5] hover:bg-[#FAFAFA] ${isActive ? "bg-[#FAFAFA] border-l-4 border-l-[#059669]" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-sm truncate flex-1">{name}</div>
                  {!!t.unread && (
                    <span className="px-1.5 py-0.5 bg-[#059669] text-white text-[10px] mono font-bold">{t.unread}</span>
                  )}
                </div>
                <div className="text-[11px] text-[#525252] mono truncate">
                  {canPII ? t.phone : maskPhone(t.phone)}
                </div>
                <div className="text-xs text-[#525252] mt-1 truncate">
                  {t.last_direction === "outbound" && <span className="text-[#0A0A0A] font-bold">You: </span>}
                  {t.last_body || "—"}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active thread */}
      <div className="bg-white border border-[#E5E5E5] overflow-hidden flex flex-col">
        {!activePhone && (
          <div className="flex-1 flex items-center justify-center text-[#525252] text-sm" data-testid="inbox-no-thread">
            Pick a conversation from the left
          </div>
        )}
        {activePhone && (
          <>
            <div className="p-3 border-b border-[#E5E5E5] flex items-center justify-between">
              <div>
                <div className="font-bold text-sm">
                  {threads.find((t) => t.phone === activePhone)?.contact_name || (canPII ? activePhone : maskPhone(activePhone))}
                </div>
                <div className="text-[11px] text-[#525252] mono">
                  {canPII ? activePhone : maskPhone(activePhone)}
                </div>
              </div>
              {threads.find((t) => t.phone === activePhone)?.matched && (
                <span className="px-2 py-0.5 border border-[#059669] text-[#059669] text-[10px] uppercase tracking-[0.2em] font-bold">
                  Existing customer
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FAFAFA]" data-testid="inbox-messages">
              {loadingMessages && <Loader2 className="w-5 h-5 animate-spin text-[#059669] mx-auto" />}
              {!loadingMessages && messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[78%] px-3 py-2 ${
                      m.direction === "outbound"
                        ? "bg-[#059669] text-white"
                        : "bg-white border border-[#E5E5E5]"
                    }`}
                  >
                    <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                    <div className={`text-[10px] mt-1 mono ${m.direction === "outbound" ? "text-white/70" : "text-[#A3A3A3]"}`}>
                      {m.direction === "outbound" && m.actor_name ? `${m.actor_name} · ` : ""}
                      {new Date(m.timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
              {!loadingMessages && messages.length === 0 && (
                <div className="text-center text-xs text-[#525252] py-8">No messages in this thread.</div>
              )}
            </div>

            <form onSubmit={sendReply} className="p-3 border-t border-[#E5E5E5] flex items-end gap-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your reply…"
                rows={2}
                data-testid="inbox-reply-input"
                className="flex-1 px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm resize-none"
                onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) sendReply(e); }}
              />
              <button
                type="submit"
                disabled={!reply.trim() || sending}
                data-testid="inbox-reply-send"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
